import { useState } from 'react';
import { Button, H1, H2, Input, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { DayPlanEvent, DayPlanItem } from '@mrrmlab/shared-types';
import { useDayPlan, useMarkCandidateDone, useQuickTodo, useRunPlanner } from '../hooks';

const SOURCE_LABEL: Record<DayPlanItem['source'], string> = {
  mail: 'Mail',
  tracking: 'Paket',
  calendar: 'Kalender',
  habit: 'Routine',
  project: 'Projekt',
  manual: 'Manuell',
  todo: 'Todo',
};

const PRIORITY_COLOR: Record<DayPlanItem['priority'], string> = {
  low: '$color5',
  normal: '$color7',
  high: '$red9',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

interface TimelineEntry {
  key: string;
  startIso: string;
  node: React.ReactElement;
}

export interface DayPlannerAction {
  label: string;
  onPress: () => void;
  hint?: string;
}

export interface DayPlannerScreenProps {
  emptyStateActions?: readonly DayPlannerAction[];
}

export function DayPlannerScreen({ emptyStateActions }: DayPlannerScreenProps = {}) {
  const query = useDayPlan();
  const runPlanner = useRunPlanner();
  const markDone = useMarkCandidateDone();
  const quickTodo = useQuickTodo();
  const [quickTitle, setQuickTitle] = useState('');

  if (query.isPending) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">Konnte den Tagesplan nicht laden: {query.error.message}</Paragraph>
        <Button onPress={() => query.refetch()}>Erneut versuchen</Button>
      </Screen>
    );
  }

  const plan = query.data;
  const timeline = buildTimeline(plan.items, plan.events, (id) => markDone.mutate(id));

  function handleQuickTodo() {
    const title = quickTitle.trim();
    if (!title) return;
    quickTodo.mutate(title, { onSuccess: () => setQuickTitle('') });
  }

  return (
    <Screen scrollable>
      <YStack gap="$2">
        <H1>Heute</H1>
        <Text fontSize="$3" opacity={0.7}>{formatDate(plan.date)}</Text>
      </YStack>

      <XStack gap="$2" alignItems="center">
        <Input
          flex={1}
          size="$4"
          placeholder="Schnell-Todo hinzufügen…"
          value={quickTitle}
          onChangeText={setQuickTitle}
          onSubmitEditing={handleQuickTodo}
          disabled={quickTodo.isPending}
        />
        <Button
          size="$4"
          disabled={quickTodo.isPending || !quickTitle.trim()}
          onPress={handleQuickTodo}
          theme="active"
        >
          {quickTodo.isPending ? <Spinner size="small" /> : '+'}
        </Button>
      </XStack>

      <XStack gap="$2" alignItems="center">
        <Button
          disabled={runPlanner.isPending}
          onPress={() => runPlanner.mutate()}
          theme="active"
        >
          {runPlanner.isPending ? 'Plane…' : 'Neu planen'}
        </Button>
        {runPlanner.isError ? (
          <Text fontSize="$2" color="$red10">{runPlanner.error.message}</Text>
        ) : runPlanner.isSuccess ? (
          <Text fontSize="$2" color="$color10">
            {timeline.length > 0
              ? `${timeline.length} Einträge geplant`
              : 'Keine Candidates verfügbar'}
          </Text>
        ) : null}
      </XStack>

      {timeline.length === 0 ? (
        <EmptyState actions={emptyStateActions} />
      ) : (
        <YStack gap="$2">
          <H2 fontSize="$6">Tagesplan</H2>
          {timeline.map((entry) => entry.node)}
        </YStack>
      )}
    </Screen>
  );
}

function EmptyState({ actions }: { actions?: readonly DayPlannerAction[] }) {
  return (
    <YStack
      gap="$3"
      paddingVertical="$5"
      paddingHorizontal="$4"
      borderRadius="$4"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$color2"
    >
      <YStack gap="$2">
        <H2 fontSize="$6">Noch nichts für heute geplant</H2>
        <Paragraph opacity={0.75}>
          Der Day-Planner zieht Aufgaben automatisch aus Mails, Paket-Trackings,
          Kalender-Terminen und Gewohnheiten. Sobald eine dieser Quellen
          eingerichtet ist und Candidates erzeugt, füllt sich die Timeline.
        </Paragraph>
      </YStack>
      {actions && actions.length > 0 ? (
        <YStack gap="$2">
          {actions.map((a) => (
            <Button
              key={a.label}
              onPress={a.onPress}
              justifyContent="flex-start"
              theme="active"
            >
              <YStack alignItems="flex-start">
                <Text fontWeight="600">{a.label}</Text>
                {a.hint ? (
                  <Text fontSize="$1" opacity={0.7}>{a.hint}</Text>
                ) : null}
              </YStack>
            </Button>
          ))}
        </YStack>
      ) : null}
    </YStack>
  );
}

function buildTimeline(
  items: DayPlanItem[],
  events: DayPlanEvent[],
  onMarkDone: (candidateId: string) => void,
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  for (const e of events) {
    entries.push({
      key: `event:${e.id}`,
      startIso: e.allDay ? `${e.startsAt.slice(0, 10)}T00:00:00.000Z` : e.startsAt,
      node: <EventRow key={`event:${e.id}`} event={e} />,
    });
  }
  for (const i of items) {
    entries.push({
      key: `item:${i.candidateId}`,
      startIso: i.plannedSlot,
      node: (
        <ItemRow
          key={`item:${i.candidateId}`}
          item={i}
          onMarkDone={() => onMarkDone(i.candidateId)}
        />
      ),
    });
  }
  entries.sort((a, b) => a.startIso.localeCompare(b.startIso));
  return entries;
}

function EventRow({ event }: { event: DayPlanEvent }) {
  return (
    <XStack
      gap="$3"
      paddingVertical="$2"
      paddingHorizontal="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$blue6"
      backgroundColor="$blue3"
    >
      <YStack width={70}>
        <Text fontSize="$2" fontWeight="700" color="$blue11">
          {event.allDay ? 'Ganztags' : formatTime(event.startsAt)}
        </Text>
        {!event.allDay ? (
          <Text fontSize="$1" opacity={0.6}>bis {formatTime(event.endsAt)}</Text>
        ) : null}
      </YStack>
      <YStack flex={1} gap="$1">
        <Text fontSize="$4" fontWeight="600">{event.title}</Text>
        {event.location ? <Text fontSize="$2" opacity={0.6}>{event.location}</Text> : null}
      </YStack>
    </XStack>
  );
}

function ItemRow({ item, onMarkDone }: { item: DayPlanItem; onMarkDone: () => void }) {
  return (
    <XStack
      gap="$3"
      paddingVertical="$2"
      paddingHorizontal="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
      alignItems="center"
    >
      <Button
        size="$3"
        circular
        chromeless
        accessibilityLabel={`Erledigt: ${item.title}`}
        onPress={onMarkDone}
      >
        ○
      </Button>
      <YStack width={70}>
        <Text fontSize="$2" fontWeight="700">{formatTime(item.plannedSlot)}</Text>
        {item.estDurationMin ? (
          <Text fontSize="$1" opacity={0.6}>{item.estDurationMin} min</Text>
        ) : null}
      </YStack>
      <YStack flex={1} gap="$1">
        <Text fontSize="$4" fontWeight="600">{item.title}</Text>
        <XStack gap="$2" alignItems="center">
          <Badge label={SOURCE_LABEL[item.source]} />
          {item.priority !== 'normal' ? (
            <Badge label={item.priority.toUpperCase()} color={PRIORITY_COLOR[item.priority]} />
          ) : null}
        </XStack>
      </YStack>
    </XStack>
  );
}

function Badge({ label, color }: { label: string; color?: string }) {
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor={color ?? '$color3'}
    >
      <Text fontSize="$1" letterSpacing={0.5} textTransform="uppercase" color={color ? 'white' : undefined}>
        {label}
      </Text>
    </XStack>
  );
}
